// powerlete-card-swipe-hint.js
//
// One-shot "these images swipe" hint on the FIRST product card of a results grid, on touch
// viewports only. Shortly after the page has settled, the card's slide row nudges left far enough
// to expose a sliver of image 2, then eases back. Competitor-inspired (Bo+Tee); see DECISIONS.md.
//
// The motion itself is pure CSS - see the `pw-card-swipe-hint` keyframes in card-gallery.liquid.
// This module only decides WHETHER and WHEN to add the class, and guarantees image 2 is actually
// painted first so the nudge never reveals a white gap.
//
// Deliberately NOT a custom element: there is exactly one hint per page load, so a module-level
// one-shot is simpler than a per-instance component and ships less code.
//
// Known limitation: the nudge assumes a left-to-right grid, exactly as carousel-loop.js does. The
// store ships no RTL locale today (markets UK/US/AU/CA); see DECISIONS.md.

const MOBILE_ONLY = '(max-width: 749px)';
const REDUCE_MOTION = '(prefers-reduced-motion: reduce)';

// The card's own slides, not a nested slideshow's. Mirrors the "be specific about HTML children
// structure" convention in slideshow-styles.liquid.
const SLIDES = ':scope > slideshow-container > slideshow-slides > slideshow-slide:not([hidden])';

// How long the card must sit still in view before the hint fires. Long enough that it reads as a
// deliberate affordance rather than part of page load, short enough to catch a shopper who is
// still looking at the top of the grid.
const HINT_DELAY_MS = 700;

// Give up on the second image rather than animate into a blank slide.
const DECODE_TIMEOUT_MS = 2000;

// Fires once per page load. Deliberately not re-armed on collection filter/morph updates.
let spent = false;

/** @param {string} query */
const matches = (query) => window.matchMedia(query).matches;

/**
 * Resolves once the browser has finished loading, so nothing here competes with the collection
 * LCP. card-gallery.liquid eager-loads only each card's FIRST image for exactly that reason, and
 * this module then asks for a second one.
 * @returns {Promise<void>}
 */
function afterLoad() {
  if (document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => window.addEventListener('load', () => resolve(), { once: true }));
}

/**
 * Resolves when `element` has been continuously in view for `delay` ms. The timer restarts if the
 * card scrolls out and back, so the hint lands on a settled card rather than one mid-flight.
 * @param {Element} element
 * @param {number} delay
 * @param {AbortSignal} signal
 * @returns {Promise<void>}
 */
function whenSettledInView(element, delay, signal) {
  return new Promise((resolve, reject) => {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;

    const cleanup = () => {
      clearTimeout(timer);
      observer.disconnect();
      signal.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(signal.reason);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer);
        if (!entry?.isIntersecting) return;

        timer = setTimeout(() => {
          cleanup();
          resolve();
        }, delay);
      },
      { threshold: 0.6 }
    );

    signal.addEventListener('abort', onAbort, { once: true });
    observer.observe(element);
  });
}

/**
 * Forces the second slide to be decoded and painted before we animate it into view.
 *
 * Two separate problems, two fixes:
 *  - `content-visibility: auto` means an off-screen slide is skipped from paint and would POP in
 *    as the nudge slides it across. Setting [actioned] flips it to `visible` through the existing
 *    CSS rules. carousel-autoplay.js does the same thing for the same reason; the attribute is
 *    read in CSS only and has no behavioural side effects in slideshow.js.
 *  - The image is `loading="lazy"` (only slide 1 is eager), so it may not have been fetched at all.
 *
 * @param {Element} slideshow
 * @param {HTMLImageElement} image
 * @returns {Promise<void>}
 */
async function paintSecondSlide(slideshow, image) {
  slideshow.setAttribute('actioned', '');
  image.loading = 'eager';
  image.fetchPriority = 'low';

  await Promise.race([
    image.decode(),
    new Promise((_, reject) => setTimeout(() => reject(new Error('decode timeout')), DECODE_TIMEOUT_MS)),
  ]);
}

async function run() {
  if (spent) return;
  spent = true;

  // Desktop already teaches the second image through hover-swap (product-card.js previewImage), so
  // the hint would be redundant there, and would fight that hover if the cursor landed on the card
  // mid-animation.
  if (!matches(MOBILE_ONLY) || matches(REDUCE_MOTION)) return;
  if (!('IntersectionObserver' in window)) return;

  // First card of the first results grid. `data-page` is emitted by main-collection.liquid;
  // requiring page 1 stops an infinite-scroll page (whose forloop index restarts at 0) from
  // qualifying as "the first card".
  const card = document.querySelector('ul.product-grid > li.product-grid__item');
  if (!(card instanceof HTMLElement) || card.dataset.page !== '1') return;

  const gallery = card.querySelector('.card-gallery');
  const slideshow = gallery?.querySelector('slideshow-component');
  if (!gallery || !slideshow) return;

  // A disabled slideshow does not swipe, so there is nothing to hint at.
  if (slideshow.getAttribute('disabled') === 'true') return;

  const slides = slideshow.querySelectorAll(SLIDES);
  if (slides.length < 2) return;

  const secondImage = slides[1]?.querySelector('img');
  if (!(secondImage instanceof HTMLImageElement)) return;

  // Touching the card cancels the hint outright: a shopper already handling it does not need to be
  // taught the gesture, and moving it under their finger would be hostile.
  const controller = new AbortController();
  const { signal } = controller;
  const cancel = () => controller.abort(new Error('user interacted'));

  card.addEventListener('pointerdown', cancel, { passive: true, signal });
  card.addEventListener('touchstart', cancel, { passive: true, signal });

  try {
    await afterLoad();
    if (signal.aborted) return;

    await paintSecondSlide(slideshow, secondImage);
    if (signal.aborted) return;

    await whenSettledInView(card, HINT_DELAY_MS, signal);
  } catch {
    // Cancelled, or the second image never decoded. Either way, do not animate.
    controller.abort();
    return;
  }

  // Drops the pre-flight cancel listeners; from here a touch stops the animation instead.
  controller.abort();

  // Re-check the viewport: a rotation or resize during the wait can have moved us onto the desktop
  // layout, where hover-swap already covers this.
  if (!matches(MOBILE_ONLY) || matches(REDUCE_MOTION)) return;

  gallery.classList.add('card-gallery--swipe-hint');

  const running = new AbortController();
  const stop = () => {
    gallery.classList.remove('card-gallery--swipe-hint');
    running.abort();
  };

  // animationend bubbles from the slides; every slide finishes together, so the first one is the
  // end of the hint. Matched by name because other animations inside the card would otherwise end
  // this one early.
  gallery.addEventListener(
    'animationend',
    (event) => {
      if (event.animationName === 'pw-card-swipe-hint') stop();
    },
    { signal: running.signal }
  );

  // Mid-animation touch: drop the class immediately. The slides snap back to their resting offset,
  // which is imperceptible next to the shopper's own gesture, and it hands the card straight back.
  card.addEventListener('pointerdown', stop, { passive: true, signal: running.signal });
  card.addEventListener('touchstart', stop, { passive: true, signal: running.signal });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run, { once: true });
} else {
  run();
}
